import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { X } from "lucide-react";
import { SelectField } from "../components/ui/select-field.js";
import { api, getAccessToken, errMsg } from "../lib/api.js";
import { logError } from "../lib/logger.js";
import { AsyncButton } from "../components/ui/async-button.js";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs.js";
import { CompVerificationQueue } from "../components/CompVerificationQueue.js";
import CompStructurePanel from "./admin/CompStructurePanel.js";
import { SearchInput } from "../components/ui/search-input.js";
import { Pagination } from "../components/ui/pagination.js";
import { usePaged } from "../lib/usePaged.js";
import { PayrollInputPanel } from "../components/payroll/PayrollInputPanel.js";

import { COMP_VERIFY_ROLES, PAYROLL_ROLES } from "@hrms/types";

function decodeRole(token: string): string {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]!));
    return (payload as Record<string, unknown>)["role"] as string ?? "";
  } catch { return ""; }
}

interface PayrollRun {
  id: string; month: number; year: number; source: string; status: string;
  processedAt: string | null; createdAt: string;
  _count: { lineItems: number };
}
interface LineItem {
  id: string; employeeCode: string; grossPay: string; netPay: string; currency: string;
  hasDiscrepancy: boolean; discrepancyNote: string | null;
  employee: { firstName: string; lastName: string } | null;
}

const statusBadge = (s: string): React.CSSProperties => {
  if (s === "VERIFIED") return { background: "var(--color-success-bg)", color: "var(--color-success-fg)" };
  if (s === "DISCREPANCIES_FOUND") return { background: "var(--color-error-bg)", color: "var(--color-error-fg)" };
  return { background: "var(--color-warning-bg)", color: "var(--color-warning-fg)" };
};

const inputStyle: React.CSSProperties = {
  padding: "8px 10px", border: "1px solid var(--color-surface-200)",
  borderRadius: "var(--radius-md)", fontSize: "14px", background: "var(--color-surface-0)",
};

// The tab keys stay as they are (notifications link to ?tab=compensation / structure);
// the labels say what each tab is for, in the order the month's payroll work happens.
const TAB_INFO = {
  input: {
    label: "Pay Inputs",
    hint: "Before payroll: what to pay this month, calculated from HRMS — payable working days, LOP, salary, salary changes, reimbursements, joiners and exits. Reconciliation checks the uploaded pay against it.",
  },
  runs: {
    label: "Payroll Uploads",
    hint: "After payroll: the final pay file from ADP / Paybooks for each month. Upload the CSV here; every line is checked against Pay Inputs.",
  },
  compensation: {
    label: "Salary Approvals",
    hint: "Employee salary (CTC) revisions waiting for Finance to verify before they take effect.",
  },
  structure: {
    label: "Salary Rule Approvals",
    hint: "Changes to the salary structure rules (Admin → Compensation Rules) waiting for Finance to verify.",
  },
} as const;

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

export default function PayrollPage() {
  const accessToken = getAccessToken() ?? "";
  const userRole = decodeRole(accessToken);
  const canAccess = PAYROLL_ROLES.has(userRole);
  const canVerifyComp = COMP_VERIFY_ROLES.has(userRole);
  // ?tab=compensation is where the "waiting for Finance verification" notification lands.
  const [searchParams, setSearchParams] = useSearchParams();
  // ?tab=structure is where "Compensation structure vN is waiting for Finance verification" lands.
  // No tab opens Payroll Input, the month's payroll inputs and reconciliation.
  const rawTab = searchParams.get("tab");
  const tab: keyof typeof TAB_INFO = canVerifyComp && (rawTab === "compensation" || rawTab === "structure") ? rawTab
    : rawTab === "runs" ? "runs" : "input";

  const [runs, setRuns] = useState<PayrollRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRun, setSelectedRun] = useState<PayrollRun | null>(null);
  const [items, setItems] = useState<LineItem[]>([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [uploadResult, setUploadResult] = useState<{ summary: { total: number; discrepancies: number; clean: number } } | null>(null);
  const [form, setForm] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    source: "MANUAL" as "ADP" | "PAYBOOKS" | "MANUAL",
    csv: "",
  });

  async function load() {
    setLoading(true);
    try {
      const res = await api.get<PayrollRun[]>("/payroll/runs");
      setRuns(res.data);
    } catch (err) { logError("PayrollPage.load", err); } finally { setLoading(false); }
  }

  useEffect(() => { if (canAccess) load(); else setLoading(false); }, []);

  // ── Runs list: search (month, year, source) + status + year, 10 per page ──
  const [runSearch, setRunSearch] = useState("");
  const [runStatus, setRunStatus] = useState("");
  const [runYear, setRunYear]     = useState("");
  const runYears = useMemo(() => [...new Set(runs.map(r => r.year))].sort((a, b) => b - a), [runs]);
  const visibleRuns = useMemo(() => {
    const q = runSearch.trim().toLowerCase();
    return runs.filter(r =>
      (!runStatus || r.status === runStatus) &&
      (!runYear || String(r.year) === runYear) &&
      (!q || `${MONTHS[r.month - 1]} ${r.year} ${r.source}`.toLowerCase().includes(q)));
  }, [runs, runSearch, runStatus, runYear]);
  const runPages = usePaged(visibleRuns, `${runSearch.trim()}|${runStatus}|${runYear}`, 10);

  // ── Employees of the open run: search (code, name) + discrepancy filter, 20 per page ──
  const [itemSearch, setItemSearch] = useState("");
  const [itemFilter, setItemFilter] = useState<"" | "ISSUES" | "CLEAN">("");
  const visibleItems = useMemo(() => {
    const q = itemSearch.trim().toLowerCase();
    return items.filter(i =>
      (!itemFilter || (itemFilter === "ISSUES" ? i.hasDiscrepancy : !i.hasDiscrepancy)) &&
      (!q || [i.employeeCode, i.employee ? `${i.employee.firstName} ${i.employee.lastName}` : ""].some(v => v.toLowerCase().includes(q))));
  }, [items, itemSearch, itemFilter]);
  const itemPages = usePaged(visibleItems, `${selectedRun?.id ?? ""}|${itemSearch.trim()}|${itemFilter}`);

  async function openRun(run: PayrollRun) {
    setSelectedRun(run);
    setItemsLoading(true);
    try {
      const url = run.status === "DISCREPANCIES_FOUND"
        ? `/payroll/runs/${run.id}/discrepancies`
        : `/payroll/runs/${run.id}/items`;
      const res = await api.get<LineItem[] | { discrepancies: LineItem[] }>(url);
      setItems(Array.isArray(res.data) ? res.data : (res.data as { discrepancies: LineItem[] }).discrepancies);
    } catch (err) { logError("PayrollPage.openRun", err); } finally { setItemsLoading(false); }
  }

  async function handleUpload() {
    setUploading(true); setError(""); setUploadResult(null);
    try {
      const res = await api.post<{ summary: { total: number; discrepancies: number; clean: number } }>("/payroll/upload", form);
      setUploadResult(res.data);
      load();
    } catch (e: unknown) {
      setError(errMsg(e) ?? "Upload failed");
      throw e;
    } finally { setUploading(false); }
  }

  if (!canAccess) return (
    <div style={{ padding: "var(--space-8)", color: "var(--color-error-fg)" }}>
      Access restricted to Payroll/Finance roles.
    </div>
  );

  // Full width on large screens so the Payroll Runs and Compensation Verification tables use the space.
  return (
    <div style={{ padding: "var(--space-8)" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "var(--color-text-900)" }}>Payroll</h1>
        {/* Upload CSV belongs to Payroll Uploads only, not the approval tabs. */}
        {tab === "runs" && (
          <button
            onClick={() => { setShowUpload(true); setUploadResult(null); setError(""); }}
            style={{ padding: "8px 16px", background: "var(--color-brand-600)", color: "#fff", border: "none", borderRadius: "var(--radius-md)", cursor: "pointer", fontWeight: 600, fontSize: 14 }}>
            Upload CSV
          </button>
        )}
      </div>

      <Tabs value={tab} onValueChange={v => setSearchParams(v === "input" ? {} : { tab: v }, { replace: true })}>
        <TabsList>
          <TabsTrigger value="input">{TAB_INFO.input.label}</TabsTrigger>
          <TabsTrigger value="runs">{TAB_INFO.runs.label}</TabsTrigger>
          {canVerifyComp && <TabsTrigger value="compensation">{TAB_INFO.compensation.label}</TabsTrigger>}
          {canVerifyComp && <TabsTrigger value="structure">{TAB_INFO.structure.label}</TabsTrigger>}
        </TabsList>
        <p className="mt-3 mb-6 text-[13px] text-surface-500">{TAB_INFO[tab].hint}</p>

      <TabsContent value="input">
        {tab === "input" && <PayrollInputPanel />}
      </TabsContent>

      <TabsContent value="compensation">
        <CompVerificationQueue />
      </TabsContent>

      {/* Salary-structure rule changes proposed in Admin > Compensation Rules, approved here by Finance. */}
      <TabsContent value="structure">
        {tab === "structure" && <CompStructurePanel verifyOnly />}
      </TabsContent>

      <TabsContent value="runs">
      {showUpload && (
        <div style={{ background: "var(--color-surface-0)", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-lg)", padding: 20, marginBottom: 24 }}>
          <h3 style={{ marginBottom: 16, fontWeight: 600, fontSize: 15 }}>Upload Payroll CSV</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 4 }}>Month</label>
              <SelectField value={form.month}
                onChange={e => setForm(f => ({ ...f, month: +e.target.value }))}>
                {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
              </SelectField>
            </div>
            <div>
              <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 4 }}>Year</label>
              <input type="number" style={{ ...inputStyle, width: "100%" }} value={form.year}
                onChange={e => setForm(f => ({ ...f, year: +e.target.value }))} />
            </div>
            <div>
              <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 4 }}>Source</label>
              <SelectField value={form.source}
                onChange={e => setForm(f => ({ ...f, source: e.target.value as "ADP" | "PAYBOOKS" | "MANUAL" }))}>
                <option>ADP</option><option>PAYBOOKS</option><option>MANUAL</option>
              </SelectField>
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, color: "var(--color-text-500)", display: "block", marginBottom: 4 }}>
              CSV content — header must include: EmployeeCode, GrossPay, NetPay, Currency
            </label>
            <textarea rows={8}
              style={{ ...inputStyle, width: "100%", boxSizing: "border-box", fontFamily: "monospace", fontSize: 13, resize: "vertical" }}
              placeholder={"EmployeeCode,GrossPay,NetPay,Currency\nIN-BLR-001,83333,70000,INR"}
              value={form.csv} onChange={e => setForm(f => ({ ...f, csv: e.target.value }))} />
          </div>
          {error && <p style={{ color: "var(--color-error)", fontSize: 13, marginBottom: 8 }}>{error}</p>}
          {uploadResult && (
            <div style={{ background: "var(--color-success-bg-subtle)", border: "1px solid var(--color-success-border)", borderRadius: "var(--radius-md)", padding: 12, marginBottom: 12, fontSize: 14 }}>
              Upload complete — Total: <strong>{uploadResult.summary.total}</strong> | Clean: <strong>{uploadResult.summary.clean}</strong> | Discrepancies: <strong style={{ color: uploadResult.summary.discrepancies > 0 ? "var(--color-error)" : undefined }}>{uploadResult.summary.discrepancies}</strong>
            </div>
          )}
          <div style={{ display: "flex", gap: 8 }}>
            <AsyncButton onClick={() => handleUpload()} disabled={uploading || !form.csv.trim()}>
              Upload & Verify
            </AsyncButton>
            <button onClick={() => setShowUpload(false)}
              style={{ padding: "8px 16px", background: "transparent", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-md)", cursor: "pointer" }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: selectedRun ? "300px 1fr" : "1fr", gap: 20 }}>
        <div>
          <h3 style={{ fontSize: 13, fontWeight: 600, marginBottom: 12, color: "var(--color-text-500)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Uploaded Payroll Files</h3>
          {!loading && runs.length > 0 && (
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <SearchInput className={selectedRun ? "w-full" : "w-64"} placeholder="Search month, year or source" aria-label="Search payroll runs"
                value={runSearch} onChange={e => setRunSearch(e.target.value)} />
              <SelectField size="sm" className={selectedRun ? "flex-1" : "w-40"} value={runStatus} onChange={e => setRunStatus(e.target.value)} aria-label="Status">
                <option value="">All statuses</option>
                <option value="VERIFIED">Verified</option>
                <option value="DISCREPANCIES_FOUND">Issues</option>
                <option value="PENDING">Pending</option>
              </SelectField>
              <SelectField size="sm" className={selectedRun ? "flex-1" : "w-32"} value={runYear} onChange={e => setRunYear(e.target.value)} aria-label="Year">
                <option value="">All years</option>
                {runYears.map(y => <option key={y} value={String(y)}>{y}</option>)}
              </SelectField>
            </div>
          )}
          {loading ? (
            <p style={{ color: "var(--color-text-400)", fontSize: 14 }}>Loading…</p>
          ) : runs.length === 0 ? (
            <p style={{ color: "var(--color-text-400)", fontSize: 14 }}>No payroll runs yet. Upload a CSV to get started.</p>
          ) : visibleRuns.length === 0 ? (
            <p style={{ color: "var(--color-text-400)", fontSize: 14 }}>No payroll runs match these filters.</p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {runPages.pageItems.map(r => (
                <div key={r.id} onClick={() => openRun(r)} style={{
                  padding: 14, background: "var(--color-surface-0)",
                  border: selectedRun?.id === r.id ? "2px solid var(--color-brand-500)" : "1px solid var(--color-surface-200)",
                  borderRadius: "var(--radius-lg)", cursor: "pointer",
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontWeight: 600, fontSize: 15 }}>{MONTHS[r.month - 1]} {r.year}</span>
                    <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 999, fontWeight: 600, ...statusBadge(r.status) }}>
                      {r.status === "DISCREPANCIES_FOUND" ? "Issues" : r.status}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: "var(--color-text-500)", marginTop: 4 }}>
                    {r.source} · {r._count.lineItems} employees
                  </div>
                </div>
              ))}
              <Pagination meta={runPages.meta} onPageChange={runPages.setPage} className="mt-2" />
            </div>
          )}
        </div>

        {selectedRun && (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <h3 style={{ fontSize: 13, fontWeight: 600, color: "var(--color-text-500)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                {selectedRun.status === "DISCREPANCIES_FOUND" ? "Discrepancies" : "All Employees"} — {MONTHS[selectedRun.month - 1]} {selectedRun.year}
              </h3>
              <button onClick={() => setSelectedRun(null)}
                style={{ fontSize: 13, background: "none", border: "none", cursor: "pointer", color: "var(--color-text-400)", display: "inline-flex", alignItems: "center" }}><X size={13} /></button>
            </div>
            {itemsLoading ? <p style={{ color: "var(--color-text-400)", fontSize: 14 }}>Loading…</p> : (
              <>
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <SearchInput className="w-72" placeholder="Search employee code or name" aria-label="Search employees in this run"
                  value={itemSearch} onChange={e => setItemSearch(e.target.value)} />
                <SelectField size="sm" className="w-48" value={itemFilter} onChange={e => setItemFilter(e.target.value as "" | "ISSUES" | "CLEAN")} aria-label="Discrepancy filter">
                  <option value="">All employees</option>
                  <option value="ISSUES">With discrepancies</option>
                  <option value="CLEAN">No discrepancies</option>
                </SelectField>
              </div>
              <div style={{ overflowX: "auto", background: "var(--color-surface-0)", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-lg)" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: "var(--color-surface-50)", borderBottom: "1px solid var(--color-surface-200)" }}>
                      {["Code","Employee","Gross Pay","Net Pay","Note"].map(h => (
                        <th key={h} style={{ textAlign: h === "Gross Pay" || h === "Net Pay" ? "right" : "left", padding: "10px 14px", color: "var(--color-text-500)", fontWeight: 600, whiteSpace: "nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleItems.length === 0 && (
                      <tr><td colSpan={5} style={{ padding: "18px 14px", textAlign: "center", color: "var(--color-text-400)" }}>No employees match these filters.</td></tr>
                    )}
                    {itemPages.pageItems.map(item => (
                      <tr key={item.id} style={{ borderBottom: "1px solid var(--color-surface-100)", background: item.hasDiscrepancy ? "var(--color-warning-bg-subtle)" : undefined }}>
                        <td style={{ padding: "9px 14px", fontFamily: "monospace", fontSize: 12 }}>{item.employeeCode}</td>
                        <td style={{ padding: "9px 14px" }}>
                          {item.employee ? `${item.employee.firstName} ${item.employee.lastName}` : <span style={{ color: "var(--color-error)" }}>Not found</span>}
                        </td>
                        <td style={{ padding: "9px 14px", textAlign: "right" }}>{Number(item.grossPay).toLocaleString()} {item.currency}</td>
                        <td style={{ padding: "9px 14px", textAlign: "right" }}>{Number(item.netPay).toLocaleString()} {item.currency}</td>
                        <td style={{ padding: "9px 14px", color: "var(--color-error)", fontSize: 12, maxWidth: 280 }}>{item.discrepancyNote ?? ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination meta={itemPages.meta} onPageChange={itemPages.setPage} />
              </>
            )}
          </div>
        )}
      </div>
      </TabsContent>
      </Tabs>
    </div>
  );
}
